import { ref, type ComputedRef } from 'vue';
import type { SortOrder } from '../internal';

export function useTableSort(params: {
  firstSortOrder: ComputedRef<SortOrder>;
  onSortChanged: (
    newSorts: Map<string, { order?: SortOrder | null; prio?: number | null }>
  ) => void;
}) {
  const sortPrio = ref<string[]>([]);
  const sortInfo = ref<Map<string, SortOrder>>(new Map());

  function calculate2WaySortOrder(previous: SortOrder | undefined) {
    if (!previous) {
      return params.firstSortOrder.value;
    }
    return previous === 'asc' ? 'desc' : 'asc';
  }

  function calculate3WaySortOrder(previous: SortOrder | undefined) {
    switch (previous) {
      case undefined:
        return params.firstSortOrder.value;
      case params.firstSortOrder.value:
        return params.firstSortOrder.value === 'asc' ? 'desc' : 'asc';
      default:
        return undefined;
    }
  }

  function onSort(columnId: string, ctrl: boolean) {
    const newSorts = new Map<string, { order?: SortOrder | null; prio?: number | null }>();
    if (ctrl) {
      if (sortInfo.value.has(columnId)) {
        const newSortOrder = calculate3WaySortOrder(sortInfo.value.get(columnId));
        if (newSortOrder) {
          sortInfo.value.set(columnId, newSortOrder);
          newSorts.set(columnId, { order: newSortOrder });
        } else {
          const idx = sortPrio.value.indexOf(columnId);
          sortPrio.value.splice(idx, 1);
          sortInfo.value.delete(columnId);
          newSorts.set(columnId, { order: null, prio: null });
          for (let i = idx; i < sortPrio.value.length; i++) {
            const otherColumnId = sortPrio.value[i];
            newSorts.set(otherColumnId, { prio: i });
          }
        }
      } else {
        sortPrio.value.push(columnId);
        sortInfo.value.set(columnId, params.firstSortOrder.value);
        newSorts.set(columnId, {
          order: params.firstSortOrder.value,
          prio: sortPrio.value.length - 1,
        });
      }
    } else {
      const newSortOrder = calculate2WaySortOrder(sortInfo.value.get(columnId));
      sortInfo.value.forEach((_, otherColumnId) =>
        newSorts.set(otherColumnId, { order: null, prio: null })
      );
      sortInfo.value.clear();
      sortPrio.value = [columnId];
      sortInfo.value.set(columnId, newSortOrder);
      newSorts.set(columnId, { order: newSortOrder, prio: 0 });
    }
    params.onSortChanged(newSorts);
  }

  return { sortPrio, sortInfo, onSort };
}
